
import { memo, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/SectionHeading";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";

// Memoize component to prevent unnecessary re-renders
const WhatIsMysteryActSection = memo(() => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  // Parallax effect for the image
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.9], [0.4, 0.6, 0.2]);

  return (
    <motion.section 
      ref={sectionRef}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
      viewport={{ once: true, margin: "-100px" }}
      className="py-32 md:py-40 relative overflow-hidden bg-gradient-to-b from-black via-black/95 to-black"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          style={{ y, opacity }}
          className="absolute inset-0 bg-[url('/lovable-uploads/bfe74481-3461-4ffe-b3ca-b14306047976.webp')] bg-cover bg-center"
        />
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-[10%] w-40 h-40 bg-anzir/10 rounded-full blur-[80px]"></div>
          <div className="absolute bottom-40 right-[15%] w-60 h-60 bg-anzir/5 rounded-full blur-[100px]"></div>
        </div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <SectionHeading
            preTitle="Discover"
            title={t('whatIs.title')}
            description={t('whatIs.description')}
            centered
            className="max-w-5xl mx-auto"
          />
        </motion.div>
        
        <motion.div 
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9 }}
          viewport={{ once: true }}
          className="relative max-w-7xl mx-auto"
        >
          <div className="grid md:grid-cols-12 gap-8 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ x: -40, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="md:col-span-5 bg-black/80 backdrop-blur-md border border-white/10 rounded-2xl p-8 md:p-10 shadow-xl"
            >
              <h3 className="text-3xl font-bold mb-6 text-white bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/80">
                {t('whatIs.experience')}
              </h3>
              <p className="text-white/80 mb-8 text-lg leading-relaxed">
                {t('whatIs.step')}
              </p>
              <motion.div
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                className="inline-block"
              >
                <Link 
                  to="/what-is-mysteryact" 
                  className="group inline-flex items-center justify-center gap-2 font-medium bg-anzir px-6 py-3 text-white rounded-full transition-all hover:bg-anzir/90 hover:shadow-lg hover:shadow-anzir/20"
                >
                  {t('whatIs.learnMore')}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>
            </motion.div>
            
            {/* Right Image - with floating effect */}
            <motion.div 
              initial={{ x: 40, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="md:col-span-7 h-80 md:h-[500px] rounded-2xl overflow-hidden relative"
            >
              <motion.div
                className="absolute inset-0 border border-white/20 rounded-2xl z-20 pointer-events-none"
                style={{
                  boxShadow: "inset 0 0 30px rgba(255,255,255,0.1), 0 0 30px rgba(0,0,0,0.5)"
                }}
              />
              
              <motion.div 
                style={{ scale }}
                className="h-full w-full relative"
              >
                <motion.img 
                  ref={imageRef}
                  initial={{ scale: 1.1 }}
                  animate={{ 
                    scale: [1.05, 1.08, 1.05],
                    y: [0, -5, 0]
                  }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 10,
                    ease: "easeInOut" 
                  }}
                  src="/lovable-uploads/bfe74481-3461-4ffe-b3ca-b14306047976.webp" 
                  alt="Fantasy adventurers discussing a mystery around a table with maps and a candle" 
                  className="w-full h-full object-cover transition-all duration-500 brightness-[1.1] contrast-[1.05]"
                  loading="lazy"
                  decoding="async"
                />
                
                {/* Floating magical particles */}
                <div className="absolute inset-0 mix-blend-screen opacity-30 pointer-events-none">
                  <div className="absolute w-full h-full bg-[radial-gradient(circle_at_center,_rgba(255,140,0,0.1)_0%,_transparent_50%)]"></div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
});

// Add display name for debugging
WhatIsMysteryActSection.displayName = "WhatIsMysteryActSection";

export default WhatIsMysteryActSection;
